import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import { N8nWorkflows } from '../../api/n8n';

const state = {
  records: [],
  nodeTypes: {
    categories: [],
    nodes: [],
    by_category: {},
  },
  executions: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingNodeTypes: false,
    isActivating: false,
    isExecuting: false,
    isFetchingExecutions: false,
  },
};

export const getters = {
  getWorkflows(_state) {
    const records = Array.isArray(_state.records) ? _state.records : [];
    return records.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  },
  getWorkflowById: _state => id => {
    return _state.records.find(workflow => workflow.id === id);
  },
  getActiveWorkflows(_state) {
    const records = Array.isArray(_state.records) ? _state.records : [];
    return records.filter(workflow => workflow.is_active);
  },
  getNodeTypes(_state) {
    return _state.nodeTypes;
  },
  getNodesByCategory: _state => category => {
    return _state.nodeTypes.by_category[category] || [];
  },
  getExecutions: _state => workflowId => {
    return _state.executions[workflowId] || [];
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  async fetch({ commit }, params = {}) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isFetching: true });
    try {
      const response = await N8nWorkflows.getAll(params);
      commit(types.default.SET_N8N_WORKFLOWS, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isFetching: false });
    }
  },

  async fetchNodeTypes({ commit }) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, {
      isFetchingNodeTypes: true,
    });
    try {
      const response = await N8nWorkflows.getNodeTypes();
      commit(types.default.SET_N8N_NODE_TYPES, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, {
        isFetchingNodeTypes: false,
      });
    }
  },

  async create({ commit }, payload) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isCreating: true });
    try {
      const response = await N8nWorkflows.create(payload);
      commit(types.default.ADD_N8N_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isCreating: false });
    }
  },

  async update({ commit }, { id, ...updateObj }) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isUpdating: true });
    try {
      const response = await N8nWorkflows.update(id, updateObj);
      commit(types.default.UPDATE_N8N_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isDeleting: true });
    try {
      await N8nWorkflows.delete(id);
      commit(types.default.DELETE_N8N_WORKFLOW, id);
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isDeleting: false });
    }
  },

  async activate({ commit }, id) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isActivating: true });
    try {
      const response = await N8nWorkflows.activate(id);
      commit(types.default.UPDATE_N8N_WORKFLOW, response.data.workflow);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isActivating: false });
    }
  },

  async deactivate({ commit }, id) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isActivating: true });
    try {
      const response = await N8nWorkflows.deactivate(id);
      commit(types.default.UPDATE_N8N_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isActivating: false });
    }
  },

  async execute({ commit }, { id, inputData, conversationId }) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isExecuting: true });
    try {
      const response = await N8nWorkflows.execute(
        id,
        inputData,
        conversationId
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isExecuting: false });
    }
  },

  async fetchExecutions({ commit }, { workflowId, limit = 50 }) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, {
      isFetchingExecutions: true,
    });
    try {
      const response = await N8nWorkflows.getExecutions(workflowId, limit);
      commit(types.default.SET_N8N_WORKFLOW_EXECUTIONS, {
        workflowId,
        executions: response.data,
      });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, {
        isFetchingExecutions: false,
      });
    }
  },

  async duplicate({ commit }, id) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isCreating: true });
    try {
      const response = await N8nWorkflows.duplicate(id);
      commit(types.default.ADD_N8N_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isCreating: false });
    }
  },

  async import({ commit }, { workflowJson, name }) {
    commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isCreating: true });
    try {
      const response = await N8nWorkflows.import(workflowJson, name);
      commit(types.default.ADD_N8N_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.default.SET_N8N_WORKFLOWS_UI_FLAG, { isCreating: false });
    }
  },

  async export(_, id) {
    const response = await N8nWorkflows.export(id);
    return response.data;
  },
};

export const mutations = {
  [types.default.SET_N8N_WORKFLOWS_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_N8N_WORKFLOWS](_state, data) {
    // Ensure records is always an array
    _state.records = Array.isArray(data) ? data : [];
  },
  [types.default.ADD_N8N_WORKFLOW](_state, data) {
    // Ensure records is an array before pushing
    if (!Array.isArray(_state.records)) {
      _state.records = [];
    }
    _state.records.push(data);
  },
  [types.default.UPDATE_N8N_WORKFLOW]: MutationHelpers.update,
  [types.default.DELETE_N8N_WORKFLOW]: MutationHelpers.destroy,
  [types.default.SET_N8N_NODE_TYPES](_state, data) {
    _state.nodeTypes = data;
  },
  [types.default.SET_N8N_WORKFLOW_EXECUTIONS](
    _state,
    { workflowId, executions }
  ) {
    _state.executions = {
      ..._state.executions,
      [workflowId]: executions,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
